import type { ListParams, PaginationInfo } from '@eo/shared';

import getApiHttpClient from '../../../httpClients/getApiHttpClient';
import type { ClientItem, SplitAllocations, UserCapacity } from './types';
import type { StaffName } from '../types';

export async function fetchUserCapacity(id: string, month: string): Promise<UserCapacity> {
  const res = await getApiHttpClient().get<UserCapacity>(`/api/v1/cfo/users/${id}/capacity`, {
    params: { month },
  });
  return res.data;
}

// Every client, not just this member's: the picker has to name who currently
// owns one so taking it reads as a move rather than a silent steal.
export async function fetchOwnedClients(
  userId: string,
  page: number | undefined,
): Promise<{ clients: ClientItem[]; pagination: PaginationInfo }> {
  const res = await getApiHttpClient().get<{ clients: ClientItem[]; pagination: PaginationInfo }>(
    '/api/v1/cfo/clients',
    { params: { ownerUserId: userId, page } },
  );
  return res.data;
}

export async function searchClients({
  search,
  page,
}: ListParams): Promise<{ clients: ClientItem[]; pagination: PaginationInfo }> {
  const res = await getApiHttpClient().get<{ clients: ClientItem[]; pagination: PaginationInfo }>(
    '/api/v1/cfo/clients',
    { params: { active: true, ...(search && { search }), page } },
  );
  return res.data;
}

// Ownership lives on the client row, so assigning from the member page is the
// same PATCH the client page makes — there is no second place it can disagree.
export async function setClientOwner(clientId: string, ownerUserId: string | null): Promise<void> {
  await getApiHttpClient().patch(`/api/v1/cfo/clients/${clientId}`, { ownerUserId });
}

// Only to name the owner a client would be moved from; the member page never
// lists staff itself.
export async function fetchStaffNames(): Promise<StaffName[]> {
  const res = await getApiHttpClient().get<{ users: StaffName[] }>('/api/v1/cfo/users', {
    params: { perPage: 100 },
  });
  return res.data.users;
}

export async function upsertSplit(body: {
  userId: string;
  month: string;
  allocations: SplitAllocations;
}): Promise<void> {
  await getApiHttpClient().put('/api/v1/cfo/capacity/splits', body);
}

export async function createTimeOff(body: {
  userId: string;
  date: string;
  hours?: number;
  notes?: string;
}): Promise<void> {
  await getApiHttpClient().post('/api/v1/cfo/capacity/time-off', body);
}

export async function removeTimeOff(id: string): Promise<void> {
  await getApiHttpClient().delete(`/api/v1/cfo/capacity/time-off/${id}`);
}
